import { getText } from '@storebrand-digital/fms-translations';
import {
  InsuranceDetailsView,
  isAlis,
  SpecialVehicleDetails,
  TrailerDetails,
  VeteranDetails,
} from 'Api/insurance';
import {
  getDiscount,
  getInsuranceValidity,
  getPriceInsuranceDetails,
} from 'Components/InsuranceDetailsSections/helpers/insuranceDetailsSections.mapToInsuranceDetailsView.helpers';
import {
  alisPolicyNumberText,
  DETAILED_PRIMARY_DISCOUNT_LABEL_FMS_KEY,
  DETAILED_PRIMARY_INSURANCE_VALIDITY_LABEL_FMS_KEY,
  FOREIGN_DATE_FORMAT,
  policyNumberText,
  STANDARD_DATE_FORMAT,
} from 'Components/InsuranceDetailsSections/insuranceDetailsSections.constants';
import {
  TfaCodeKey,
  TfaCodes,
  TfaRegistry,
} from 'Components/InsuranceDetailsSections/insuranceDetailsSections.types';
import { AgreementCode } from 'Constants/agreementCodes.constants';
import { isAfter, isEqual, parse } from 'date-fns';
import { formatMoney } from 'Utils/formatting/formatting.utils';
import { getInsuranceProductName } from 'Utils/products.utils';

export function mapBaseVehicleValuesToInsuranceDetailsView(
  insurance: SpecialVehicleDetails | TrailerDetails | VeteranDetails,
  tfaRegistry: TfaRegistry[] | undefined,
  cwpDetailsToggle: boolean,
): Omit<InsuranceDetailsView, 'secondary'> {
  return {
    insuranceProduct: insurance.insuranceProduct,
    agreementProduct: insurance.agreementProduct,
    policyNumber: insurance.policyNumber,
    riskList: insurance.riskList,
    productName: getInsuranceProductName(insurance.productName),
    organizationPillar: insurance.organizationPillar,
    canChange: insurance.canChange,
    coverage: insurance.coverage,
    isInRenewal: insurance.isInRenewal,
    isDeregistered: insurance.isDeregistered,
    insuranceStatus: insurance.insuranceStatus,
    title: [insurance.description],
    primary: [
      {
        label: getText(DETAILED_PRIMARY_INSURANCE_VALIDITY_LABEL_FMS_KEY),
        value: getInsuranceValidity(insurance),
      },
      {
        label: getText(
          isAlis(insurance.systemName)
            ? alisPolicyNumberText
            : policyNumberText,
        ),
        value: insurance.policyNumber,
      },
      {
        label: getText('detailed.primary.priceVehicle.label'),
        value: getPriceInsuranceDetails(
          insurance,
          'detailed.primary.priceVehicle.value',
          cwpDetailsToggle,
        ),
      },
      {
        label: getText('detailed.primary.tfa.label'),
        value: getTfaPrice(
          insurance.tfaCode,
          insurance.outerIssueDate,
          insurance.agreementProduct,
          insurance.isDeregistered,
          tfaRegistry,
        ),
      },
      {
        label: getText(DETAILED_PRIMARY_DISCOUNT_LABEL_FMS_KEY),
        value: getDiscount(insurance.agreementProduct),
      },
    ],
  };
}

export function getTfaPrice(
  currentTfaCode: string,
  issueDate: string,
  agreementProduct: AgreementCode,
  isDeregistered: boolean,
  tfaRegistry: TfaRegistry[] | undefined,
): string | undefined {
  if (isDeregistered) {
    return getText('detailed.primary.tfa.isDeregistered');
  }

  if (agreementProduct === AgreementCode.EXTERNAL_PARTNER_FINN) {
    return getText('detailed.primary.finnCarInsurance.tfa');
  }
  const tfaCodes = findCorrectActiveDateIndex(issueDate, tfaRegistry);
  if (tfaCodes === undefined) {
    return undefined;
  }
  const yearlyPrice = getYearlyTfaPrice(currentTfaCode, tfaCodes);
  if (yearlyPrice === undefined) {
    return undefined;
  }
  return getText('detailed.primary.tfa.value', {
    insert: { tfaPrice: yearlyPrice },
  });
}

function findCorrectActiveDateIndex(
  issueDate: string,
  tfaRegistryList: TfaRegistry[] | undefined,
): TfaCodes | undefined {
  /* issueDate and activeDate have different time formate */
  if (tfaRegistryList === undefined) {
    return undefined;
  }
  const currentIssueDate = parse(issueDate, STANDARD_DATE_FORMAT, new Date());
  for (let i = tfaRegistryList.length - 1; i >= 0; i -= 1) {
    const dateToParse = tfaRegistryList[i]?.activeDate;
    const activeDate =
      dateToParse && parse(dateToParse, FOREIGN_DATE_FORMAT, new Date());
    if (
      activeDate &&
      (isEqual(currentIssueDate, activeDate) ||
        isAfter(currentIssueDate, activeDate))
    ) {
      /* start searching the most recent dates */
      return tfaRegistryList[i].tfaCodes;
    }
  }
  /* if not found, return undefined and remove from mapper */
  return undefined;
}

export function getYearlyTfaPrice(
  currentTfaCode: string,
  tfaCodes: TfaCodes,
): string | undefined {
  switch (currentTfaCode) {
    case TfaCodeKey.tfa000: {
      return formatMoney(tfaCodes?.tfa000?.yearly);
    }
    case TfaCodeKey.tfa100: {
      return formatMoney(tfaCodes?.tfa100?.yearly);
    }
    case TfaCodeKey.tfa200: {
      return formatMoney(tfaCodes?.tfa200?.yearly);
    }
    case TfaCodeKey.tfa300: {
      return formatMoney(tfaCodes?.tfa300?.yearly);
    }
    case TfaCodeKey.tfa400: {
      return formatMoney(tfaCodes?.tfa400?.yearly);
    }
    case TfaCodeKey.tfa999: {
      return formatMoney(tfaCodes?.tfa999?.yearly);
    }
    default:
      return undefined;
  }
}
